import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { FC } from 'react';
import Banner from '@/components/UI/Banner';
import { useForm, Controller } from 'react-hook-form@latest';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import { ID } from '@/model/Common.model.ts';
import { useNavigate } from 'react-router-dom';
import { RouteService } from '@/support/RouteService.ts';
import _isEmpty from 'lodash/isEmpty';
import { _isNotEmpty } from '@/littledash.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { OperationAnimalsMoveV1Request } from '@/model/Animal.model.ts';
import { errorToast, successToast } from '@/helpers.tsx';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';

interface PresetMappingConfirmationModalProps {
  presetMappingPayload: OperationAnimalsMoveV1Request;
  duplicateCageNames: boolean;
  targetStudyId: ID;
  sourceStudyId: ID;
  measurementsToBeDropped: Map<string, Array<string>>;
  measurementsToBeAdded: Array<string>;
  closeModal: () => void;
}

interface FormData {
  duplicateCageNames: boolean;
}

export const PresetMappingConfirmationModal: FC<PresetMappingConfirmationModalProps> = ({
  presetMappingPayload,
  duplicateCageNames,
  targetStudyId,
  sourceStudyId,
  measurementsToBeDropped,
  measurementsToBeAdded,
  closeModal,
}) => {
  const { handleSubmit, control } = useForm({
    defaultValues: {
      duplicateCageNames: duplicateCageNames ?? false,
    },
  });

  const {
    invoke: postAnimalsMove,
    response: animalsMovePOSTResponse,
    loading: postingMoveAnimals,
  } = useApiHook({
    endpoint: 'POST /api/v1/operations/study-animal-move',
    body: {
      old_study_api_id: presetMappingPayload.old_study_api_id,
      new_study_api_id: presetMappingPayload.new_study_api_id,
      animal_api_ids: presetMappingPayload.animal_api_ids,
      variables: presetMappingPayload.variables,
    },
    invokeOnInit: false,
    options: { onError: { toast: false, capture: false, throw: true } },
  });

  const navigate = useNavigate();

  const submit = async (data: FormData) => {
    try {
      await postAnimalsMove({
        body: {
          old_study_api_id: presetMappingPayload.old_study_api_id,
          new_study_api_id: presetMappingPayload.new_study_api_id,
          animal_api_ids: presetMappingPayload.animal_api_ids,
          variables: presetMappingPayload.variables,
        },
      });
      closeModal();
      if (data.duplicateCageNames) {
        navigate(
          RouteService.web({
            route: 'studies.cages',
            path: { id: targetStudyId },
          }).route
        );
      } else {
        navigate(
          RouteService.web({
            route: 'studies.show',
            path: { id: sourceStudyId },
          }).route
        );
      }
      successToast('Animals moved successfully');
    } catch (err) {
      const ivError = new InVivoError('Could not move animals', {
        slug: 'move-animals',
        cause: err,
      });
      ExceptionHandler.captureException(ivError);
      errorToast('Could not move animals');
    }
  };

  return (
    <ModalContainer size="narrow" className="bg-white">
      <ModalHeader title="Confirm mapping and move animals?" className="pa3 bb b--moon-gray w-100" />
      <div className="pl3 pr3 mv3 near-black">
        <p>
          Mapped measurements, doses, samples and observations will remain intact.{' '}
          <span className="fw5">
            All groups, alerts, tracking dates or dropped measurements will be permanently removed.
          </span>{' '}
          <span className="red">This cannot be undone.</span>
        </p>
        <div className="pb4">
          {_isEmpty(measurementsToBeDropped) && _isEmpty(measurementsToBeAdded) && (
            <div className="pt4">
              <p>
                <span className="fw3">All measurements mapped.</span>
              </p>
            </div>
          )}
          {measurementsToBeDropped.size > 0 && (
            <div className="pt4">
              <h4 className="fw7 pb2">Measurements to be dropped</h4>
              <p className="fw2 red pb2">The following measurements will be deleted:</p>
              <ul className="list pl4" style={{ listStyleType: 'disc' }}>
                {Array.from(measurementsToBeDropped).map(([output, inputs]) => (
                  <li key={output}>
                    {inputs.map((input) => (
                      <li key={input}>
                        <span className="fw2">{input}</span>
                        <span className="mid-gray">{' - ' + output}</span>
                      </li>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {_isNotEmpty(measurementsToBeAdded) && (
            <div className="pt4">
              <h4 className="fw7 pb2">Measurements to be added</h4>
              <p className="fw2 pb2">The following measurements will be added to the preset of the new study:</p>
              <ul className="list pl4" style={{ listStyleType: 'disc' }}>
                {measurementsToBeAdded.map((output) => (
                  <li key={output}>
                    <span className="fw2">{output}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {duplicateCageNames && (
          <Banner info dismiss={false} className="mw6 mb3">
            <h3 className="normal lh-title f5 pb2" data-testid="metadata-settings__empty-banner--title">
              There will be duplicate cage names in the recipient study. <span className="fw5">Update the names</span>{' '}
              in the new study to ensure uniqueness.
            </h3>
          </Banner>
        )}
        <form onSubmit={handleSubmit(submit)}>
          <div className="mt3">
            <Controller
              name="duplicateCageNames"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Checkbox label="Take me to the new study" checked={value} onChange={onChange} />
              )}
            />
          </div>
        </form>
      </div>
      <ModalActions
        submitButtonProps={{ disabled: postingMoveAnimals || animalsMovePOSTResponse?.type === 'error' }}
        className="pa3 bt b--moon-gray"
        onSubmit={handleSubmit(submit)}
        onCancel={closeModal}
        submitBtnText="Confirm"
        cancelBtnText="Cancel"
      />
    </ModalContainer>
  );
};
